<script setup>
import {HpoUpdateTrackerQuery} from "@/graphql/queries/hpo/updateTracker.graphql"
import {useQuery} from "@vue/apollo-composable";

import {useLastResult} from "@/graphql/composable";
import App from "@/components/App";
import {HpoMemberListQuery} from "@/graphql/queries/hpo/member.graphql"
import {HpoRole} from "../choices";
import {computed, provide, ref, watch} from "vue";
import {isEmsMember, isIzsDispatching, isIzsWorker} from "../utils";
import EmsDashboard from "./EmsDashboard.vue";
import {HpoPatientCountsQuery} from "@/graphql/queries/hpo/counts.graphql"
import {lg} from "../../../utils/breakpoints";
import {mdiGestureTap} from "@mdi/js";
import ContactPointDashboard from "./ContactPointDashboard.vue";
import IzsDashboard from "./IzsDashboard.vue";
import Members from "@/components/hpo/dashboard/members/Members.vue";
import HospitalScreenDashboard from "./HospitalScreenDashboard.vue";
import EmergencyDashboard from "./EmergencyDashboard.vue";
import InfolineDashboard from "../infoLine/dashboard/Dashboard.vue";



const props = defineProps({
  hpo: {
    type: Object,
    required: true
  },
  countries: {
    type: Array,
    required: true,
  },
  member: {
    type: Object,
    required: true
  },
})

const showOverlay = ref(false);

provide('hpoMember', props.member)

// UpdateTracker
const {result: updateTrackerResult} = useQuery(
  HpoUpdateTrackerQuery,
  {hpoId: props.hpo.id},
  {
    pollInterval: 5 * 1000,
    fetchPolicy: 'network-only'
  })
const updateTracker = useLastResult(updateTrackerResult, {}, data => data.hpo)

const personsTrackerTimestamp = computed(() => {
  if (updateTracker.value.personsTracker) {
    return updateTracker.value.personsTracker
  }
})
const hospitalsTrackerTimestamp = computed(() => {
  if (updateTracker.value.hospitalsTracker) {
    return updateTracker.value.hospitalsTracker
  }
})

// Patient Counts
const {
  result: patientCountsResult,
  loading: patientCountsLoading,
} = useQuery(HpoPatientCountsQuery, () => ({hpoId: props.hpo.id, updateTimestamp: personsTrackerTimestamp.value}), {
  fetchPolicy: 'network-only'
})
const patientCounts = useLastResult(patientCountsResult, null, (data) => data.patientCounts)

// Member list
const {
  result: memberListResult,
  loading: memberListLoading,
  refetch: refetchMemberList
} = useQuery(HpoMemberListQuery, {hpoId: props.hpo.id})
const hpoPassword = useLastResult(memberListResult, null, data => data.hpoPassword)
const hpoPin = useLastResult(memberListResult, null, data => data.hpoPin)


watch(updateTracker, (newValue, oldValue) => {
  if (Object.values(oldValue).length === 0) {
    return
  }

  // MEMBERS TRACKER
  if (newValue.membersTracker !== oldValue.membersTracker) {
    refetchMemberList();
  }
})

const mobileView = computed(() => {
  return !lg.value
})

const chipProperties = computed(() => {
  if (mobileView.value === true) {
    return {
      variant: 'elevated',
      size: 'default',
      density: 'compact'
    }
  }
  return {
    variant: 'elevated',
    size: 'large',
    density: 'default'
  }
})
</script>


<template>
  <App>
    <template #nav-controls>
      <Members v-if="[HpoRole.ZOS, HpoRole.KOPIS, HpoRole.IOS].includes(member.role)"
               :hpoId="hpo.id"
               :member="member"
               :hpoPassword="hpoPassword"
               :hospitalsTrackerTimestamp="hospitalsTrackerTimestamp"/>
    </template>

    <div v-if="showOverlay" class="loading-overlay">
      <v-container>
        <v-row class="fill-height">
          <v-col class="d-flex justify-center align-center">
            <v-card variant="elevated">
              <v-card-title>
                <h1 class="text-center text-wrap">{{ pgettext('title', 'Application start') }}</h1>
              </v-card-title>
              <v-card-actions>
                <v-btn @click="showOverlay=false"
                       variant="elevated"
                       class="font-weight-bold pa-4 h-auto"
                       size="x-large"
                       :prepend-icon="mdiGestureTap">
                  <div class="text-wrap">
                    {{ pgettext('action', 'Click here to start the application') }}
                  </div>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </div>


    <div v-else class="py-3 py-md-4 px-3 px-md-4 px-lg-6 hpo-dashboard-container">
      <!--            <template v-if="isDesktop">-->
      <!--                <Teleport to=".sidebar__user">-->
      <!--                    <HpoTitle :hpo="hpo" :member="member"/>-->
      <!--                </Teleport>-->
      <!--            </template>-->

      <template v-if="patientCounts && updateTracker">
        <EmsDashboard v-if="isEmsMember(member.role)"
                      :hpo="hpo"
                      :member="member"
                      :patientCounts="patientCounts"
                      :patientCountsLoading="patientCountsLoading"
                      :hpoPassword="hpoPassword"
                      :countries="countries"
                      :hpoPin="hpoPin"
                      :mobileView="mobileView"
                      :chipProperties="chipProperties"
                      :personsTrackerTimestamp="personsTrackerTimestamp"
                      :hospitalsTrackerTimestamp="hospitalsTrackerTimestamp"/>

        <ContactPointDashboard v-else-if="member.role === HpoRole.CONTACT_POINT"
                               :hpo="hpo"
                               :member="member"
                               :patientCounts="patientCounts"
                               :patientCountsLoading="patientCountsLoading"
                               :hpoPassword="hpoPassword"
                               :countries="countries"
                               :mobileView="mobileView"
                               :chipProperties="chipProperties"
                               :personsTrackerTimestamp="personsTrackerTimestamp"
                               :hospitalsTrackerTimestamp="hospitalsTrackerTimestamp"/>

        <HospitalScreenDashboard v-else-if="member.role === HpoRole.HOSPITAL_SCREEN"
                                 :hpo="hpo"
                                 :member="member"
                                 :patientCounts="patientCounts"
                                 :patientCountsLoading="patientCountsLoading"
                                 :hpoPassword="hpoPassword"
                                 :countries="countries"
                                 :mobileView="mobileView"
                                 :chipProperties="chipProperties"
                                 :personsTrackerTimestamp="personsTrackerTimestamp"
                                 :hospitalsTrackerTimestamp="hospitalsTrackerTimestamp"/>

        <EmergencyDashboard v-else-if="member.role === HpoRole.ER"
                            :hpo="hpo"
                            :member="member"
                            :patientCounts="patientCounts"
                            :patientCountsLoading="patientCountsLoading"
                            :hpoPassword="hpoPassword"
                            :countries="countries"
                            :mobileView="mobileView"
                            :chipProperties="chipProperties"
                            :personsTrackerTimestamp="personsTrackerTimestamp"
                            :hospitalsTrackerTimestamp="hospitalsTrackerTimestamp"/>


        <ContactPointDashboard v-else-if="member.role === HpoRole.ER"
                               :hpo="hpo"
                               :member="member"
                               :patientCounts="patientCounts"
                               :patientCountsLoading="patientCountsLoading"
                               :hpoPassword="hpoPassword"
                               :countries="countries"
                               :mobileView="mobileView"
                               :chipProperties="chipProperties"
                               :personsTrackerTimestamp="personsTrackerTimestamp"
                               :hospitalsTrackerTimestamp="hospitalsTrackerTimestamp"/>


        <IzsDashboard
          v-else-if="isIzsDispatching(member.role) || member.role === HpoRole.VIEWER || isIzsWorker(member.role)"
          :hpo="hpo"
          :member="member"
          :patientCounts="patientCounts"
          :patientCountsLoading="patientCountsLoading"
          :hpoPassword="hpoPassword"
          :countries="countries"
          :mobileView="mobileView"
          :chipProperties="chipProperties"
          :personsTrackerTimestamp="personsTrackerTimestamp"/>

        <InfolineDashboard
          v-else-if="member.role = HpoRole.INFOLINE"
          :hpo="hpo"
          :member="member"
          :patientCounts="patientCounts"
          :patientCountsLoading="patientCountsLoading"
          :hpoPassword="hpoPassword"
          :countries="countries"
          :mobileView="mobileView"
          :chipProperties="chipProperties"
          :personsTrackerTimestamp="personsTrackerTimestamp"/>
      </template>
    </div>
  </App>
</template>
